// @flow
import * as React from 'react';
import styled from 'styled-components';
import Datetime from 'react-datetime';
import indexStyle from 'react-datetime/css/react-datetime.css';

import moment from 'moment';

import { Label, FormGroup, Tooltip } from '../../Form';
import colors from "../../../assets/css/colors";
import ArrowLeft from '../../../assets/images/ArrowLeft.svg';
import ArrowRight from '../../../assets/images/ArrowRight.svg';
import ArrowUp from '../../../assets/images/ArrowUp.svg';
import ArrowDown from '../../../assets/images/ArrowDown.svg';

import Lock from '../../../components/Icons/Lock';
import Calendar from '../../../components/Icons/Calendar';
import { getStartDraftDate } from "../../../helpers";

import { BorderedIcon } from "../../../components";

const DatetimeWrapper = styled.div`
	position: relative;
	width: 100%;

	svg {
		position: absolute;
		top: 10px;
		right: 10px;
		pointer-events: none;
	}
`;

const DatetimeStyled = styled(Datetime)`
	${indexStyle}
	width: 100%;

	input {
		color: ${colors.darkGrey};
		cursor: pointer;
		font-weight: bold;
		font-size: 12px;
		height: 36px;
		border: 1px solid ${colors.lightGrey};
		background-color: #FFFFFF;
		width: 100%;
		box-shadow: 2px 0px 9px 2px #efeeee;
		padding-left: 1em;

		&:disabled {
			opacity: .5;
			margin-left: 1em;
		}
	}

	.rdtPicker {
		border-radius: 5px;
		box-shadow: ${colors.box_shadow};
	}

	td {
		text-align: center;
		vertical-align: middle;
	}

	.rdtPicker td.rdtActive {
		background-color: ${colors.nrlGreen};
		color: ${colors.black};
		border-radius: 5px;

		&:hover {
			background-color: ${colors.nrlGreenHover};
			color: ${colors.black};
		}
	}

	.rdtPicker td.rdtActive.rdtToday:before {
		border-bottom-color: ${colors.black};
	}

	.rdtPicker td.rdtToday:before {
		border-bottom-color: ${colors.nrlGreen};
	}



	.rdtNext span,
	.rdtPrev span
	 {
	}

	.rdtSwitch {
		font-weight: 700;
		vertical-align: middle;
	}

	.rdtPicker .rdtTimeToggle,
	.rdtPicker .rdtTime .rdtSwitch {
		outline: none;
		border: none;
		background: ${colors.nrlDarkGreen};
		color: ${colors.nrlGreen};
		border-radius: 3px;
		font-weight: bold;
		font-size: 12px;
		letter-spacing: 0.5px;
		line-height: 1.5;
		padding: 10px;
		height: 36px;
		cursor: pointer;
		width: auto;
		&:hover {
			background: ${colors.nrlDarkGreenHover};
		}
		&:hover {
			background: ${colors.nrlDarkGreen};
		}
	}

	thead td {
		padding-bottom: 10px;
	}

	thead > tr:first-child > th {
		padding: 10px 0;
	}

	.rdtPicker thead tr:first-child th:hover,
	.rdtPicker thead tr:first-child th:hover {
		background-color: transparent;
	}

	.rdtPrev span {
		height: 16px;
		text-indent: -999em;
		background-image: url(${ArrowLeft});
		background-size: contain;
		background-position: right center;
		background-repeat: no-repeat;
	}

	.rdtNext span {
		height: 16px;
		text-indent: -999em;
		background-image: url(${ArrowRight});
		background-size: contain;
		background-position: left center;
		background-repeat: no-repeat;
	}

	.rdtCounter .rdtBtn:first-child {
		text-indent: -999em;
		background-image: url(${ArrowUp});
		background-repeat: no-repeat;
		background-position: center center;
	}

	.rdtCounter .rdtBtn:last-child {
		text-indent: -999em;
		background-image: url(${ArrowDown});
		background-repeat: no-repeat;
		background-position: center center;
	}

	.rdtCounter .rdtBtn:hover {
		background-color: transparent;
	}
`;

// const getValidDates = (currentData, maxDate) => {
// 	const
// 		start_date = getStartDraftDate(),
// 		yesterday = start_date.clone().subtract(1, 'day');

// 	maxDate = moment(maxDate).clone().subtract(1, 'day');

// 	return currentData.isAfter(yesterday) && currentData.isBefore(maxDate);
// };

const getValidDates = (currentData, maxDate) => {
	const start_date = getStartDraftDate();  // Feb 3 2025
	const startDate = start_date.clone().startOf("day");

	const max = moment(maxDate).clone().endOf("day");

	return currentData.isSameOrAfter(startDate) && currentData.isSameOrBefore(max);
};

const getValidTimes = currentDateTime => {
	const
		start_date = moment.unix(process.env.REACT_APP_START_DRAFT_DATE),
		startDate = moment(start_date).format("YYYY-MM-DD"),
		currentDate = moment(currentDateTime).format("YYYY-MM-DD");

	// date is today, so only allow future times

	if (currentDate === startDate) {
		return {
			hours: {
				min: start_date.hours(),
				max: 24,
				step: 1,
			},
			minutes: {
				min: 0,
				max: 60,
				step: 5,
			},
		};
	}

	// date is in the future, so allow all times
	return {};
};

type Props = {
	label: string,
	name: string,
	type: string,
	placeholder: string,
	defaultValue: number,
	is_disabled: boolean,
	input: Object,
	maxDate: string,
	tooltip: {
		title: string,
		description: string,
	},
};
export const FormDateField = ({
	label,
	name,
	type,
	placeholder,
	tooltip,
	defaultValue,
	is_disabled,
	input,
	maxDate,
}: Props) => (
	<div>
		<Label htmlFor={name}>
			{label}
		</Label>
		<FormGroup>
			{is_disabled ? (
				<BorderedIcon padding='.25em'>
					<Lock size="2" />
				</BorderedIcon>
			) : ''}
			<DatetimeWrapper>
				<DatetimeStyled
					name={name}
					type={type}
					placeholder={placeholder}
					defaultValue={moment(defaultValue)}
					dateFormat="ddd, D MMM YYYY"
					timeFormat="h:mmA"
					isValidDate={currentData => getValidDates(currentData, maxDate)}
					timeConstraints={getValidTimes(input.value)}
					{...input}
					inputProps={{ disabled: is_disabled, readOnly: true }}
				/>
				<Calendar />
			</DatetimeWrapper>
			<Tooltip {...tooltip} />
		</FormGroup>

	</div>
);

export default FormDateField;


// WEBPACK FOOTER //
// ./src/components/Form/FormDateField/index.js